import React, { useEffect, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  DragOverlay,
} from "@dnd-kit/core";
import { api, CHANNEL_COLORS, ORDER_STAGES } from "@/lib/api";
import { PIPELINE } from "@/constants/testIds";
import { toast } from "sonner";

function Card({ inquiry }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: inquiry.id,
    data: { inquiry },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-testid={PIPELINE.card(inquiry.id)}
      className={`bg-white border border-stone-200 rounded-xl p-3 shadow-sm cursor-grab active:cursor-grabbing ${
        isDragging ? "opacity-30" : "hover:-translate-y-0.5 hover:border-stone-300"
      } transition-all duration-200`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-stone-800 truncate">
          {inquiry.customer_name}
        </div>
        <span className={`heritage-badge ${CHANNEL_COLORS[inquiry.channel] || ""}`}>
          {inquiry.channel}
        </span>
      </div>
      <div className="text-xs text-stone-500 mt-1 line-clamp-2">
        {inquiry.message || "—"}
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-[10px] uppercase tracking-widest text-stone-400">
          {inquiry.inquiry_type}
        </span>
        <span className="text-[10px] text-stone-400">
          {new Date(inquiry.created_at).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
}

function Column({ stage, items }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <div
      ref={setNodeRef}
      data-testid={PIPELINE.column(stage)}
      className={`min-w-[280px] w-72 bg-stone-50 border border-stone-200 rounded-2xl p-3 flex flex-col gap-2 ${
        isOver ? "ring-2 ring-terracotta/40 bg-terracotta/5" : ""
      }`}
    >
      <div className="flex items-center justify-between px-1 pb-1">
        <div className="font-serif text-lg text-stone-800">{stage}</div>
        <div className="text-xs text-stone-500 bg-white border border-stone-200 rounded-full px-2 py-0.5">
          {items.length}
        </div>
      </div>
      <div className="flex-1 flex flex-col gap-2 min-h-[100px]">
        {items.map((i) => (
          <Card key={i.id} inquiry={i} />
        ))}
      </div>
    </div>
  );
}

export default function Pipeline() {
  const [inquiries, setInquiries] = useState([]);
  const [dragging, setDragging] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const load = async () => {
    try {
      const { data } = await api.get("/inquiries", { params: { limit: 500 } });
      setInquiries(data || []);
    } catch (err) {
      console.warn('Saheli: request failed', err);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const grouped = ORDER_STAGES.reduce((acc, s) => {
    acc[s] = inquiries.filter((i) => i.status === s);
    return acc;
  }, {});

  const onDragStart = (e) => {
    setDragging(e.active.data.current?.inquiry || null);
  };

  const onDragEnd = async (e) => {
    setDragging(null);
    const stage = e.over?.id;
    const inq = e.active?.data?.current?.inquiry;
    if (!stage || !inq || inq.status === stage) return;
    // optimistic
    setInquiries((prev) =>
      prev.map((i) => (i.id === inq.id ? { ...i, status: stage } : i)),
    );
    try {
      await api.patch(`/inquiries/${inq.id}/status`, { status: stage });
      toast.success(`Moved ${inq.customer_name} → ${stage}`);
    } catch {
      toast.error("Move failed");
      load();
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <div className="overline">Order pipeline</div>
        <h1 className="font-serif text-4xl text-stone-900 mt-1">Kanban</h1>
        <p className="text-stone-500 text-sm mt-2">
          Drag any inquiry across stages — status updates in real time.
        </p>
      </div>

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div
          data-testid={PIPELINE.board}
          className="flex gap-4 overflow-x-auto pb-4 kanban-scroll"
        >
          {ORDER_STAGES.map((s) => (
            <Column key={s} stage={s} items={grouped[s] || []} />
          ))}
        </div>
        <DragOverlay>
          {dragging ? (
            <div className="bg-white border border-terracotta/40 rounded-xl p-3 shadow-md w-64">
              <div className="text-sm font-medium text-stone-800 truncate">
                {dragging.customer_name}
              </div>
              <div className="text-xs text-stone-500 mt-1 line-clamp-2">
                {dragging.message}
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
